import React, { Suspense, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { useFormikContext, getIn } from 'formik';
import { lookupsJson, FarmRiskType } from '@ourbranch/lookups';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';

import editIcon from 'core/assets/svg/edit.svg';
import withDatePicker from 'core/components/with-date-picker';
import CollapsibleCard from 'core/components/collapsible-card';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { LabelTooltip } from 'core/components/label-tooltip';
import { appConfig } from 'brand-config';
import {
  InlandMarineForm,
  InlandFloodCoverageForm,
  UnscheduledPersonalPropertyForm,
  SwimmingPoolForm
} from '../sub-risks';
import { Banner } from 'common/components/banner';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import { useFarmOpenSections } from 'common/hooks/farm-open-sections-context';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { useStore } from 'core/store';
import { BrandFormField } from 'common/components/farm/brand-form-field';
import { getDwellingConstructionTypeOptions } from 'common/helpers/farm-helpers';

import useStyles from '../risk.styles';

const heatingOptions = lookupsJson.farm.primaryHeatingSourceOptions;
const earthquakeDwellingOptions = lookupsJson.farm.earthquakeDeductible;
const waterBackupOptions = lookupsJson.farm.waterBackupLimit;
const windHailDeductibleOptions = lookupsJson.farm.windstormOrHail;
const propertyTypeOptions = lookupsJson.farm.propertyType;

const FO4 = ({ item, index, fieldName, removeRisk, setRiskFieldnameToEdit, canRemoveRisk, locationAddress }) => {
  const classes = useStyles();
  const {
    offer: { selectedOption }
  } = useStore();
  const constructionTypeOptions = getDwellingConstructionTypeOptions(FarmRiskType.Fo4, selectedOption);
  const { setFieldValue, values } = useFormikContext();
  const { disableForm: disableRiskForm } = useActiveFormState(fieldName);
  const { isCurrentSectionOpen, toggleSection } = useFarmOpenSections(fieldName);
  const { inPolicyRoute } = useRouteHelper();
  const riskType = FarmRiskType.Fo4;
  const address = locationAddress;
  const hasBeenRated = !!item.id;
  const state = address?.state;

  const riskCoverages = getIn(values, fieldName)?.coverages || {};

  const checkIfCoverageIsEnabled = (coverageName) =>
    !appConfig.farm[state]?.risks[riskType]?.hiddenCoverages?.includes(coverageName);

  // Address synchronization - respects the risk's useApplicantAddress checkbox (per PR #28262)
  useEffect(() => {
    const farmLocation = values.farmLocations?.[0];

    // Only sync if this risk has opted to use the farm location address
    if (item.useApplicantAddress && farmLocation?.address) {
      // Sync this risk's address with farm location address
      setFieldValue(`${fieldName}.address.address`, farmLocation.address.address);
      setFieldValue(`${fieldName}.address.address2`, farmLocation.address.address2);
      setFieldValue(`${fieldName}.address.city`, farmLocation.address.city);
      setFieldValue(`${fieldName}.address.state`, farmLocation.address.state);
      setFieldValue(`${fieldName}.address.zip`, farmLocation.address.zip);
      setFieldValue(`${fieldName}.address.county`, farmLocation.address.county);
    }
    // When useApplicantAddress is unchecked, allow independent address (don't force sync)
  }, [
    item.useApplicantAddress, // Key dependency: only sync when checkbox is checked
    values.farmLocations?.[0]?.address?.address,
    values.farmLocations?.[0]?.address?.address2,
    values.farmLocations?.[0]?.address?.city,
    values.farmLocations?.[0]?.address?.state,
    values.farmLocations?.[0]?.address?.zip,
    values.farmLocations?.[0]?.address?.county,
    fieldName,
    setFieldValue
  ]);

  return (
    <CollapsibleCard
      content={
        <div className={classes.header}>
          <div className={classes.headerRow}>
            <Label type="infoTitleSmall">{`FO-4 Renters: ${propertyTypeOptions.find((option) => option.id === item?.building?.propertyType)?.value}`}</Label>
            <div className={classes.divider} />
            <Label type="infoSubtitleSmall">{item.description}</Label>
            <IconButton onClick={() => setRiskFieldnameToEdit(fieldName)} className={classes.editButton}>
              <img className={classes.editIcon} src={editIcon} alt="Edit row" />
            </IconButton>
          </div>
        </div>
      }
      isRemovable={canRemoveRisk}
      onDelete={() => removeRisk(index)}
      disableRemoveButton={disableRiskForm}
      open={isCurrentSectionOpen}
      onChange={toggleSection}
      id={fieldName}
    >
      <Suspense
        fallback={
          <Grid container justifyContent="space-between" className={classes.riskCard} spacing={2}>
            <Grid container className={classes.loaderContainer} alignItems="center" justifyContent="center">
              <CircularProgress className={classes.loader} />
            </Grid>
          </Grid>
        }
      >
        {!hasBeenRated && (
          <Banner
            variant="warning"
            title="Data for this dwelling or structure isn't available—please enter the details manually."
            subtitle={
              inPolicyRoute
                ? "This information won't auto-fill, even if you preview changes."
                : "This information won't auto-fill, even if you update the offer."
            }
          />
        )}
        <Grid container justifyContent="space-between" className={classes.riskCard} spacing={2}>
          <Grid item xs={12}>
            <Label type="infoTitleSmall">Property</Label>
          </Grid>
          <BrandFormField
            xs={6}
            type="select"
            options={constructionTypeOptions}
            id={`${fieldName}.building.constructionType`}
            name={`${fieldName}.building.constructionType`}
            mode="light"
            label="Construction Type"
            disabled={disableRiskForm}
          />
          <Grid item xs={6}>
            <LabelTooltip
              label="Solid Fuel Burning Appliances"
              tooltip={{
                label: 'More Info',
                onHoverText:
                  'Solid Fuel or wood heat examples include standard fireplace, fireplace with insert, indoor solid fuel or wood heat (including pellet stoves), outside solid fuel or wood heat. Barrel or Homemade stoves are Not Acceptable.'
              }}
              mode="light"
            >
              <BrandFormField
                type="numeric"
                id={`${fieldName}.building.numSolidFuelBurningAppliances`}
                name={`${fieldName}.building.numSolidFuelBurningAppliances`}
                mode="light"
                format={{
                  allowNegative: false
                }}
                disabled={disableRiskForm}
              />
            </LabelTooltip>
          </Grid>
          <BrandFormField
            xs={6}
            id={`${fieldName}.building.primaryHeatingSource`}
            name={`${fieldName}.building.primaryHeatingSource`}
            type="select"
            options={heatingOptions}
            mode="light"
            label="Primary Heating"
            disabled={disableRiskForm}
          />
          <BrandFormField
            xs={6}
            id={`${fieldName}.connectedHome.theftPreventionDeviceType`}
            name={`${fieldName}.connectedHome.theftPreventionDeviceType`}
            type="checkbox"
            mode="light"
            label="Theft protective device"
            disabled={disableRiskForm}
            checkedValue="centralStation"
            uncheckedValue="none"
          />
          <BrandFormField
            xs={6}
            id={`${fieldName}.connectedHome.fireProtectiveDeviceType`}
            name={`${fieldName}.connectedHome.fireProtectiveDeviceType`}
            type="checkbox"
            mode="light"
            label="Fire protective device"
            disabled={disableRiskForm}
            checkedValue="centralStation"
            uncheckedValue="none"
          />

          <BrandFormField
            xs={6}
            name={`${fieldName}.connectedHome.internetMonitoring`}
            id={`${fieldName}.connectedHome.internetMonitoring`}
            type="checkbox"
            label="Internet connected monitoring device(s)?"
            mode="light"
            disabled={disableRiskForm}
          />
        </Grid>

        {hasBeenRated && (
          <Grid container spacing={2} className={classes.coverageCard}>
            <Grid item xs={12} className={classes.bottomMargin}>
              <Label type="formSubTitle">Base coverages</Label>
            </Grid>
            <Grid item xs={6}>
              <LabelTooltip
                label="Personal Property (C)"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'If something happens to personal property, like theft or damage due to fire, this covers those items.'
                }}
              >
                <Field
                  id={`${fieldName}.coverages.coverageC`}
                  name={`${fieldName}.coverages.coverageC`}
                  type="numeric"
                  mode="dark"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disabled={disableRiskForm}
                  onChange={(val) => {
                    if (val) {
                      // coverageD is always 20% of coverageC
                      setFieldValue(`${fieldName}.coverages.coverageD`, val * 0.2);
                    }
                  }}
                />
              </LabelTooltip>
            </Grid>
            <Grid item xs={6}>
              <LabelTooltip
                label="Additional Living Cost (D)"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'If the home becomes uninhabitable due to some kind of covered loss, this coverage provides temporary housing up to the amount entered here.'
                }}
              >
                <Field
                  id={`${fieldName}.coverages.coverageD`}
                  name={`${fieldName}.coverages.coverageD`}
                  type="numeric"
                  mode="dark"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disabled
                />
              </LabelTooltip>
            </Grid>

            <Grid item xs={12} className={classes.bottomMargin}>
              <Label type="formSubTitle" className={classes.sectionDivider}>
                Deductibles
              </Label>
            </Grid>
            <BrandFormField
              xs={6}
              id={`${fieldName}.coverages.deductibleAllOther`}
              name={`${fieldName}.coverages.deductibleAllOther`}
              type="select"
              mode="dark"
              label="All peril deductible"
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
            />
            <BrandFormField
              xs={6}
              id={`${fieldName}.coverages.deductibleWindHail`}
              name={`${fieldName}.coverages.deductibleWindHail`}
              type="select"
              options={windHailDeductibleOptions}
              mode="dark"
              label="Wind & Hail deductible"
              disabled={disableRiskForm}
              riskType={riskType}
              coverages={riskCoverages}
              state={state}
              classes={classes}
              coverage={riskCoverages?.coverageC}
              description="Wind and hail deductible must match an All Peril when it's value is greater than 2% of coverage C"
            />
            <Grid item xs={12} className={classes.bottomMargin}>
              <Label type="formSubTitle" className={classes.sectionDivider}>
                Optional Coverages
              </Label>
            </Grid>

            <BrandFormField
              xs={6}
              id={`${fieldName}.coverages.deductibleEarthquake`}
              name={`${fieldName}.coverages.deductibleEarthquake`}
              type="select"
              options={earthquakeDwellingOptions}
              mode="dark"
              label="Earthquake contents"
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
            />

            <BrandFormField
              xs={checkIfCoverageIsEnabled('deductibleEarthquake') ? 6 : 12}
              id={`${fieldName}.coverages.coverageWaterBackup`}
              name={`${fieldName}.coverages.coverageWaterBackup`}
              type="select"
              options={waterBackupOptions}
              mode="dark"
              label="Water back up & sump discharge/overflow"
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
            />
            <Grid container spacing={2} className={classes.bottomMargin}>
              <BrandFormField
                name={`${fieldName}.coverages.expandedDebrisRemoval`}
                id={`${fieldName}.coverages.expandedDebrisRemoval`}
                type="checkbox"
                label="Expanded debris removal"
                mode="dark"
                xs={4}
                disabled={disableRiskForm}
                riskType={riskType}
                state={state}
              />
              <BrandFormField
                name={`${fieldName}.coverages.repairRebuildRequirement`}
                id={`${fieldName}.coverages.repairRebuildRequirement`}
                type="checkbox"
                label="Repair or rebuilding requirement"
                mode="dark"
                xs={4}
                disabled
              />
              <BrandFormField
                name={`${fieldName}.coverages.personalPropertyReplCost`}
                id={`${fieldName}.coverages.personalPropertyReplCost`}
                type="checkbox"
                label="Personal property replacement cost"
                mode="dark"
                xs={4}
                disabled
              />
            </Grid>
            <UnscheduledPersonalPropertyForm
              fieldName={`${fieldName}.coverages`}
              disabled={disableRiskForm}
              state={state}
              riskType={riskType}
            />
            {checkIfCoverageIsEnabled('swimmingPoolsCoverage') && (
              <SwimmingPoolForm fieldName={fieldName} disabled={disableRiskForm} />
            )}
            <InlandMarineForm
              fieldName={`${fieldName}.coverages.scheduleCoverages`}
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
              coverages={item.coverages}
            />
            {checkIfCoverageIsEnabled('inlandFloodCoverage') && (
              <InlandFloodCoverageForm fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />
            )}
          </Grid>
        )}
      </Suspense>
    </CollapsibleCard>
  );
};

FO4.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  fieldName: PropTypes.string.isRequired,
  canRemoveRisk: PropTypes.bool.isRequired,
  removeRisk: PropTypes.func.isRequired,
  setRiskFieldnameToEdit: PropTypes.func.isRequired,
  locationAddress: PropTypes.object.isRequired
};

export default withDatePicker(FO4);
